<script lang="ts" setup>
    import '../../../css/choice.css';   
    import {onMounted,watch,computed,reactive, onUnmounted} from 'vue';  
    import {popAlert,ModalStyles} from '../../lib/Notification';   
    import ActionFoldingPanel from "../../components/ActionFoldingPanel.vue";  
    import ClaimList from '../Billing/Subcomponents/ClaimList.vue'; 
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import {ButtonDisplaySize,ButtonDisplayStyle,LayoutOrientation} from '../../Enums/UiEnums'; 
    import {ErrorReturnMessage,getPayPlanSetup,createPaymentPlan,getPayPlanInstallments} from '../../lib/Api/PaymentPlan';
    import {getCompanySettingNumber,getCompanySetting} from '../../lib/Api/Claim';
    import {InstallmentType,IPatientNameDob,IPatientPaymentPlan,IPaymentPlanCreate,IPlanSummary,UpdateTitleBar,closeVueModal,PlanMaximumCount,PlanMaximumAmount,IInstallmentDisplay,PlanSettingItem,RecurringClassLabel} from '../../lib/Models/PaymentPlanModel';  
    import {IClaim,ClaimDisplay,ClaimTableWidth,ClaimColumnDefinitions,ClaimSimpleLists} from '../../lib/Models/ClaimModel';  
    import {prettyCurrency} from '../../lib/Formatting/Number'; 
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import TextInput from '../../components/TextInput.vue'; 
    import IntegerInput from '../../components/IntegerInput.vue';
    import CurrencyInput from '../../components/CurrencyInput.vue';
    import RadioButtonInput from '../../components/RadioButtonInput.vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import SimpleList from '../../components/SimpleList.vue';
    import PaymentPlanCreditCard from '../Billing/PaymentPlanCreditCard.vue';
    import {getNoTzToday} from "../../lib/DateUtils";
    import {initPinia} from '../../lib/PiniaMgr';
    import {usePaymentPlanNewStore} from '../../lib/Stores/PaymentPlanNewStore';
    import {useSimpleListStore} from '../../lib/Stores/SimpleListStore'; 
    import {ActionButtonStyle} from '../../lib/Types/Types';  
  
    enum FieldReactivity {
        None,
        Amount,
        Count
    }

    const paymentPlanNewStore = usePaymentPlanNewStore(initPinia());
    const simpleListStore = useSimpleListStore(initPinia());
    const claimBaseMessage = "Patient Balance Claims";
    const selectClaim = true;
    const planDetailsWidth = ClaimTableWidth + ClaimColumnDefinitions.Payer +  ClaimColumnDefinitions.Status + (25/16);
    const formData = reactive <
    {      
        paymentPlans?: IPatientPaymentPlan[],
        patientInformation?: IPatientNameDob,
        patientClaims?: IClaim[],
        selectedPatientClaims?: IClaim[],
        newPaymentPlan: IPaymentPlanCreate,
        planSummary?: IPlanSummary[],
        existingPlans: {total: number, message: string},
        patientClaimInformation: string,
        planErrorMessage: {isError: boolean, message: string},
        planSettings: {MinimumAmt: number, MaximumAmt: number, MinimumCnt: number, MaximumCnt: number, Recurring: string},
        window: { footerHeight: number, windowPadding: number,workareaHeight: number},
        modalDisplayAddCard: boolean,
        cardInformation?: string,
        refreshCards: number,
        InstallmentReactivity: FieldReactivity
    }>({      
        newPaymentPlan: {patientId: 0,responsiblePartyEID: 0,totalAmount: 0,writeOffAdjustmentAmount: 0,installmentType: 2,installmentStartDate: getNoTzToday(), installmentAmount: 0, numberInstallments: 0,recurringPayments: false, excludeClaimsFromStatements: false, doNotSendStatement: false, claimList: []},
        existingPlans: {total: 0, message: ""},
        patientClaimInformation: claimBaseMessage,
        planErrorMessage: {isError: true, message: ""},
        planSettings: {MinimumAmt: 0, MaximumAmt: 0, MinimumCnt: 0, MaximumCnt: 0,  Recurring: "true"},
        window: {footerHeight: 2.5, windowPadding: 1, workareaHeight: 8}, 
        modalDisplayAddCard: false,
        refreshCards: 0,
        InstallmentReactivity: FieldReactivity.None
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled: number,
        patientId: number,
        refreshCards: number
    }>(),
    {
        saveEnabled: 0,
        patientId: 0,
        refreshCards: 0
    }); 
    async function onload() : Promise<void>  {
        formData.newPaymentPlan.patientId = props.patientId;
        const where_clause = "patient_id^" + props.patientId.toString();
        const list1 = simpleListStore.getSimpleWhereList(ClaimSimpleLists.RespParty,where_clause);  
        const list2 = simpleListStore.getSimpleWhereList(ClaimSimpleLists.CreditCard,where_clause);  
        
        await Promise.all([list1,list2]);
        refreshData(); 
    }
    onload();
    onMounted(() => {  
        resetHeights();
        window.addEventListener('resize', resetHeights);
        
    });
    onUnmounted(() => {  
        window.removeEventListener("resize", resetHeights);
    }); 
    watch(() => props.refreshCards, (newID, oldID) => {
        if ((newID !== oldID) && (newID > 0))  {
            formData.refreshCards = newID;
        }
    });
    const planAbsoluteMaximum = computed<number>(()=>  {
        if (formData.planSettings.MaximumAmt > 0) { 
            return formData.planSettings.MaximumAmt;
        }
        return PlanMaximumAmount;
    });
    
    const recurringDisabled = computed<boolean>(()=>  { 
        if ((!formData.cardInformation) || (formData.cardInformation === "")) {
            return true;
        }
        if (formData.planSettings.Recurring === 'true') {
            return false;
        }
        return true;
    });
    const recurringClass = computed<string>(()=>  {  
        if ((!formData.cardInformation) || (formData.cardInformation === "")) {
            return RecurringClassLabel.Disabled
        }
        if (recurringDisabled.value) {
            return RecurringClassLabel.Disabled
        }
        return RecurringClassLabel.Enabled
    });
    const planDisabled = computed<boolean>(()=>  {
        if (formData.patientInformation?.patientRHC) { 
            return true;
        }
        return false;
    });
    const installmentDisabled = computed<boolean>(()=> {
        if (!formData.newPaymentPlan.totalAmount || formData.newPaymentPlan.totalAmount < formData.planSettings.MinimumAmt) {
            return true;
        }
        return false;
    })
    const maximumInstallments = computed<number>(()=>  {
        if (formData.newPaymentPlan.installmentType == InstallmentType.Monthly) {
            return formData.planSettings.MaximumCnt;
        }
        return Math.ceil(formData.planSettings.MaximumCnt * 4.3); // There are 4.3 weeks per month.
    });
    const respPartyListLoaded = computed<boolean>(()=> {
        if (!simpleListStore) {
            return false;
        }
        if (!simpleListStore.listResult[ClaimSimpleLists.RespParty]) {
            return false;
        }
        if (simpleListStore.listResult[ClaimSimpleLists.RespParty].length === 0) {
            return false;
        }
        return true;
    });
  
    async function refreshData() : Promise<void>  {
        paymentPlanNewStore.resetClaims();
        formData.planSummary = [];
        const [minimumAmt,maximumAmt,minimumCnt,maximumCnt,recurring] = 
            await Promise.all([
                getCompanySettingNumber("PPLAN", PlanSettingItem.MinPlanAmount),
                getCompanySettingNumber("PPLAN", PlanSettingItem.MaxPlanAmount),
                getCompanySettingNumber("PPLAN", PlanSettingItem.MinInstallmentMonths),
                getCompanySettingNumber("PPLAN", PlanSettingItem.MaxInstallmentMonths),
                getCompanySetting("PPLAN", PlanSettingItem.RecurringPayments)
            ]);
        formData.planSettings.MinimumAmt = minimumAmt;
        formData.planSettings.MaximumAmt = maximumAmt;
        formData.planSettings.MinimumCnt = minimumCnt;
        formData.planSettings.MaximumCnt = maximumCnt;
        formData.planSettings.Recurring = recurring;
        formData.newPaymentPlan = {patientId: props.patientId,responsiblePartyEID: 0,totalAmount: 0,writeOffAdjustmentAmount: 0,installmentType: 2,installmentStartDate: getNoTzToday(), installmentAmount: 0, numberInstallments: 0,recurringPayments: false, excludeClaimsFromStatements: false, doNotSendStatement: false, claimList: []};
        const respGet = await getPayPlanSetup(props.patientId); 
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.setup,"Payment Plan Setup",ModalStyles.danger);
            return;
        } 
        formData.patientInformation = respGet.success.patientInformation;
        formData.paymentPlans = respGet.success.patientPlans; 
        formData.patientClaims = respGet.success.patientClaims; 
        formData.newPaymentPlan.responsiblePartyEID = respGet.success.patientInformation.responsiblePartyEID ?? 0;
        formData.newPaymentPlan.recurringPayments = false;
        formData.existingPlans.total = formData.paymentPlans.map(item => item.remainingTotal).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        formData.existingPlans.message =`Existing Payment Plans${(formData.existingPlans.total == 0 ? "" : " - Remaining Total: " + prettyCurrency(formData.existingPlans.total))}`;
        UpdateTitleBar(props.patientId,formData.patientInformation.patientName ?? "",formData.patientInformation.patientDOB ?? "",formData.patientInformation.patientRHC ?? "");
        validateDetailFields();
    }
    function resetHeights(): void { 
        const windowHeight = (window.innerHeight / 16); 
        formData.window.workareaHeight = windowHeight - formData.window.footerHeight - formData.window.windowPadding;
    }   
    async function addCreditCard() : Promise<void> {
        formData.modalDisplayAddCard = true;
    }
    async function savePaymentPlan() : Promise<void> {
        formData.newPaymentPlan.claimList = formData.selectedPatientClaims ?? []; 
        const returnResults = await createPaymentPlan(formData.newPaymentPlan); 
        const messageTitle = "Save Payment Plan";
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? ErrorReturnMessage.save,messageTitle,ModalStyles.danger);
            return;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return;
        }  
        await refreshData(); 
    }
    async function closeWindow() : Promise<void> { 
        window.removeEventListener("resize", resetHeights);
        closeVueModal();
    }
    function updateClaimMessaging() : void {
        formData.patientClaimInformation = `${claimBaseMessage} - ${prettyCurrency(paymentPlanNewStore.TotalBalanceSelected)}/${prettyCurrency(paymentPlanNewStore.TotalBalance)}`;
    }
    function resetPlanTotal() {
        formData.newPaymentPlan.totalAmount = paymentPlanNewStore.TotalBalanceSelected;
        formData.newPaymentPlan.installmentAmount = 0;
        formData.newPaymentPlan.numberInstallments = 0;
    }
    function selectedClaims (itemList : any) : void {
        formData.selectedPatientClaims = itemList.map((item: number) => ({ClaimID: +item})); 
        updateClaimMessaging();
        resetPlanTotal();
        validateDetailFields();
    }
    function validatePlanTotal() : void {
        validateDetailFields();
    }
    function ValidatePlanDate() : void { 
        validateDetailFields();
    }
    function validatePlanInstallment(newValue : number) : void {
        if (formData.InstallmentReactivity === FieldReactivity.Amount) {
            formData.newPaymentPlan.installmentAmount = newValue;
            formData.newPaymentPlan.numberInstallments = 0;
            formData.InstallmentReactivity = FieldReactivity.None;
            validateDetailFields();
        }
    }
    function validateInstallmentCount(newValue : number) : void {
        if (formData.InstallmentReactivity === FieldReactivity.Count) {
            formData.newPaymentPlan.numberInstallments = newValue;
            formData.newPaymentPlan.installmentAmount = 0;
            formData.InstallmentReactivity = FieldReactivity.None;
            if ((!formData.newPaymentPlan.numberInstallments) || (formData.newPaymentPlan.numberInstallments === 0)) {
                return;
            }
            formData.newPaymentPlan.installmentAmount = +(formData.newPaymentPlan.totalAmount / formData.newPaymentPlan.numberInstallments).toFixed(2);
            validateDetailFields();
        }
    }
    function setCalculationReactivity(newReactivity : FieldReactivity ) : void { 
        formData.InstallmentReactivity = newReactivity;
    }
    function validateDetailFields() : void {
        formData.planSummary = [];
        if (!simpleListStore.listResult[ClaimSimpleLists.RespParty]) {
            setErrorMessage(`Missing responsible party for patient, unable to create a payment plan.`);
            return;
        }
        if (simpleListStore.listResult[ClaimSimpleLists.RespParty].length === 0) {
            setErrorMessage(`Missing responsible party for patient, unable to create a payment plan.`);
            return;
        }
        if ((!formData.newPaymentPlan.totalAmount) || (formData.newPaymentPlan.totalAmount < formData.planSettings.MinimumAmt)) {
            setErrorMessage(`Plan total amount must be greater than or equal to ${prettyCurrency(formData.planSettings.MinimumAmt)}`);
            formData.newPaymentPlan.numberInstallments = 0;
            formData.newPaymentPlan.installmentAmount = 0;
            return;
        }
        if (formData.newPaymentPlan.totalAmount > PlanMaximumAmount) {
            setErrorMessage(`Plan total amount must be less than or equal to ${prettyCurrency(PlanMaximumAmount)}`);
            formData.newPaymentPlan.numberInstallments = 0;
            formData.newPaymentPlan.installmentAmount = 0;
            return;
        }
        if ((formData.planSettings.MaximumAmt > 0) && (formData.newPaymentPlan.totalAmount > formData.planSettings.MaximumAmt)) {
            setErrorMessage(`Plan total amount must be less than or equal to ${prettyCurrency(formData.planSettings.MaximumAmt)}`);
            formData.newPaymentPlan.numberInstallments = 0;
            formData.newPaymentPlan.installmentAmount = 0;
            return;
        }
        if ((formData.newPaymentPlan.totalAmount / formData.newPaymentPlan.installmentAmount > formData.planSettings.MaximumCnt)) {
            setErrorMessage(`Installment amount cannot be less than ${prettyCurrency((formData.newPaymentPlan.totalAmount /  formData.planSettings.MaximumCnt))} for Plan total of ${prettyCurrency(formData.newPaymentPlan.totalAmount)}`);
            formData.newPaymentPlan.numberInstallments = 0;
            formData.newPaymentPlan.installmentAmount = 0;
            return;
        } 
        if ((formData.newPaymentPlan.totalAmount / formData.newPaymentPlan.installmentAmount > PlanMaximumCount)) {
            setErrorMessage(`Installment amount cannot be less then ${prettyCurrency((formData.newPaymentPlan.totalAmount / PlanMaximumCount))} for Plan total of ${prettyCurrency(formData.newPaymentPlan.totalAmount)}`);
            formData.newPaymentPlan.numberInstallments = 0;
            formData.newPaymentPlan.installmentAmount = 0;
            return;
        }        
        if (!formData.newPaymentPlan.installmentStartDate) {
            setErrorMessage("Plan start date is required.");
            return;
        }
        const today = new Date(getNoTzToday()); 
        const new_date = (formData.newPaymentPlan?.installmentStartDate ? new Date(formData.newPaymentPlan.installmentStartDate) : today);
        if (new_date < today) { 
            setErrorMessage("Installment Start cannot start prior to today.");
            return;
        }        
        if ((!formData.newPaymentPlan.installmentAmount) || (formData.newPaymentPlan.installmentAmount < .01)) { 
            setErrorMessage("Installment amount must be greater than zero.");
            return;
        }       
        if (formData.newPaymentPlan.installmentAmount > formData.newPaymentPlan.totalAmount) { 
            setErrorMessage("Installment amount cannot be greater than plan total.");
            return;
        }     
        if (formData.newPaymentPlan.numberInstallments > PlanMaximumCount) { 
            setErrorMessage(`Installment count cannot be greater than ${PlanMaximumCount}`);
            return;
        } 
        setErrorMessage("");
        calculateSummary();
    }
    function setErrorMessage(new_message : string) : void {
        formData.planErrorMessage.message = new_message;
        formData.planErrorMessage.isError = (new_message !== "");
    }
    
    async function calculateSummary() : Promise<void> { 
        const installmentDisplay : IInstallmentDisplay = {InstallmentType: formData.newPaymentPlan.installmentType, PlanTotal: formData.newPaymentPlan.totalAmount, InstallmentStartDate: formData.newPaymentPlan.installmentStartDate,InstallmentAmount: formData.newPaymentPlan.installmentAmount, NumberInstallments: formData.newPaymentPlan.numberInstallments}
        const returnResults = await getPayPlanInstallments(installmentDisplay);  
        
        if (returnResults.error) { 
            formData.planErrorMessage.isError = true;
            formData.planErrorMessage.message = returnResults.error?.error ?? ErrorReturnMessage.installmentDisplay;  
            return;
        } 
        if (returnResults.success) {
            formData.planSummary = returnResults.success;
            formData.newPaymentPlan.numberInstallments = formData.planSummary.length; 
            return;
        }
        formData.planSummary = [];
        formData.newPaymentPlan.numberInstallments = 0;
    }
    function onChangeRespParty(selectedItem : any) : void {
        if (!formData.newPaymentPlan) {
            return;
        }
        formData.newPaymentPlan.responsiblePartyEID = selectedItem.value;
    }
    function onDisplayCreditCard(updateCreditCard: boolean, paymentMethodId: number, card_information: string) : void {
        formData.modalDisplayAddCard = false;
        if (updateCreditCard) {
            formData.newPaymentPlan.paymentMethodId = paymentMethodId;
            formData.cardInformation = card_information;
            formData.newPaymentPlan.recurringPayments = (card_information !== "" && !recurringDisabled.value);
        }
        else {
            formData.newPaymentPlan.recurringPayments = false;
        }
    }
    function SetSendStatement(_send : boolean) : void {
        if (formData.newPaymentPlan.doNotSendStatement) {
            formData.newPaymentPlan.excludeClaimsFromStatements = false;
        }        
    }
    function SetExcludeStatement(_excluded: boolean) : void { 
        if (formData.newPaymentPlan.excludeClaimsFromStatements) {
            formData.newPaymentPlan.doNotSendStatement = false;
        }
    }
                                
</script> 
 
<style scoped>
    @import url('../../../css/paymentPlanGrid.css');
</style>
<template>
    <div v-if="planDisabled" class="skinnyscroll pt-3" :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight + 'rem')}">
        <span class="text-danger fst-italic">
            I am sorry to report that patient {{formData.patientInformation?.patientName}} is deceased as of {{formData.patientInformation?.patientRHC}}, therefore new payment plan cannot be created.
        </span> 
    </div>
    <div v-if="!planDisabled" class="skinnyscroll pt-3" :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight + 'rem')}">
        <div v-if="(formData.paymentPlans?.length ?? 0) > 0">
            <ActionFoldingPanel :panelTitle="formData.existingPlans.message"> 
                <template #body>  
                    <div class="skinnyscroll label-isalus-small" :style="{ 'width': (planDetailsWidth + 'rem')}">
                        <div class="grid-container-plan">
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Status</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Total</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Remaining</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Installment</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Start</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">End</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Due</div>
                            <div class="text-center label-plan pp-grid-border-top pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">Comment</div>
                        </div>
                        <div class="grid-container-plan" v-if="(formData.paymentPlans?.length ?? 0) > 0" v-for="(planRecord,index) in formData.paymentPlans">
                            <div class="text-center value-plan pp-grid-border-bottom pp-grid-border-left">{{planRecord.statusDescription}}</div>
                            <div class="text-end value-plan pp-grid-border-bottom pp-grid-border-left">{{prettyCurrency(planRecord.totalAmount)}}</div>
                            <div class="text-end value-plan pp-grid-border-bottom pp-grid-border-left">{{prettyCurrency(planRecord.remainingTotal)}}</div>
                            <div class="text-end value-plan pp-grid-border-bottom pp-grid-border-left">{{prettyCurrency(planRecord.installmentAmount)}}</div>
                            <div class="text-center value-plan pp-grid-border-bottom pp-grid-border-left">{{planRecord.startDate}}</div>
                            <div class="text-center value-plan pp-grid-border-bottom pp-grid-border-left">{{planRecord.endDate}}</div>
                            <div class="text-center value-plan pp-grid-border-bottom pp-grid-border-left">{{planRecord.dueDate}}</div>
                            <div class="value-plan pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">{{planRecord.comment}}</div>
                        </div>                    
                    </div>
                </template> 
            </ActionFoldingPanel>  
        </div>
        <div v-if="(formData.patientClaims?.length ?? 0) > 0">
            <ActionFoldingPanel :panelTitle="formData.patientClaimInformation"> 
                <template #body>
                    <ClaimList 
                        :saveEnabled="props.saveEnabled"
                        :selection="selectClaim"
                        :claimList = "formData.patientClaims"
                        :display="ClaimDisplay.PaymentPlan"                 
                        :displayProcedures="false"
                        :displayPayments="false"
                        :displayComments="false"
                        @selectedItems:model-value="selectedClaims" 
                    /> 
                </template> 
            </ActionFoldingPanel>  
        </div>
        <ActionFoldingPanel panelTitle="Payment Plan Details"> 
            <template #body>
                <div class="skinnyscroll label-isalus-small" :style="{'width': (planDetailsWidth + 'rem')}">
                    <div class="grid-container-details">
                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-top pp-grid-border-left">Responsible Party</div>
                        <div class="details-span-3 pp-grid-border-bottom pp-grid-border-top pp-grid-border-left pp-grid-border-right">
                            <SimpleList v-if="respPartyListLoaded"
                                :listName="ClaimSimpleLists.RespParty"
                                :includeHeader="true"
                                :includeBlank="false" 
                                v-model:selected="formData.newPaymentPlan.responsiblePartyEID"
                                @onChange="onChangeRespParty" 
                            />
                        </div>

                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-left">Plan Total</div>
                        <div class="value-details pp-grid-border-bottom pp-grid-border-left">
                            <CurrencyInput
                                v-model:numberValue="formData.newPaymentPlan.totalAmount" 
                                :maxLength=12 
                                :startingRange="formData.planSettings.MinimumAmt"
                                :endingRange="planAbsoluteMaximum"
                                :positive=true
                                :required=true
                                :cssFramework = "'vanilla'"
                                @change:numberValue="() => validatePlanTotal()"
                            />
                        </div>
                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-left">Credit Card</div>
                        <div class="value-details pt-2 text-center pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">
                            {{formData.cardInformation  ?? ""}}
                        </div>

                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-left">Installment Amount</div>
                        <div class="value-details pp-grid-border-bottom pp-grid-border-left">
                            <CurrencyInput
                                :numberValue="formData.newPaymentPlan.installmentAmount" 
                                :disabled="installmentDisabled"
                                :maxLength=12 
                                :startingRange=0.01
                                :endingRange="formData.newPaymentPlan.totalAmount"
                                :positive=true  
                                :cssFramework = "'vanilla'"
                                @focus="(evt) => setCalculationReactivity(FieldReactivity.Amount)"
                                @blur="(evt) => setCalculationReactivity(FieldReactivity.None)"
                                @change:numberValue="(strValue) => validatePlanInstallment(strValue ?? 0)"
                            />    
                        </div>
                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-left">Number of Installments</div>
                        <div class="value-details pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">
                            <IntegerInput 
                                :numberValue="formData.newPaymentPlan.numberInstallments" 
                                :disabled="installmentDisabled"
                                :maxLength=4 
                                :startingRange="formData.planSettings.MinimumCnt"
                                :endingRange="maximumInstallments"
                                :positive=true
                                LeftAddon="fa-solid fa-hashtag"
                                :cssFramework = "'vanilla'" 
                                @focus="(evt) => setCalculationReactivity(FieldReactivity.Count)"
                                @blur="(evt) => setCalculationReactivity(FieldReactivity.None)"
                                @change:numberValue="(strValue) => validateInstallmentCount(strValue ?? 0)"
                            /> 
                        </div>
                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-left">Installment Start</div>
                        <div class="value-details pp-grid-border-bottom pp-grid-border-left">
                            <DatePickerNoTZ
                                v-model:value="formData.newPaymentPlan.installmentStartDate" 
                                :clearable="false" 
                                :required="true"
                                :errorDisplay="false" 
                                :cssFramework="'vanilla'"
                                @update:value="()=>ValidatePlanDate()"
                                /> 
                        </div>
                        <div class="label-details pt-2 pp-grid-border-bottom pp-grid-border-left">Installment Type</div>
                        <div class="value-details text-center pt-2 pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">
                            <RadioButtonInput 
                                :value="InstallmentType.Monthly" 
                                v-model="formData.newPaymentPlan.installmentType" 
                                :layout="LayoutOrientation.Horz"
                                @update:model-value="validateDetailFields()">  
                                <div class="label-isalus-small">Monthly</div>
                            </RadioButtonInput>
                            <RadioButtonInput 
                                :value="InstallmentType.Weekly" 
                                v-model="formData.newPaymentPlan.installmentType" 
                                :layout="LayoutOrientation.Horz" 
                                @update:model-value="validateDetailFields()">                    
                                <div class="label-isalus-small">Weekly</div>
                            </RadioButtonInput>
                            <RadioButtonInput 
                                :value="InstallmentType.BiWeekly" 
                                v-model="formData.newPaymentPlan.installmentType" 
                                :layout="LayoutOrientation.Horz" 
                                @update:model-value="validateDetailFields()">                    
                                <div class="label-isalus-small">Biweekly</div>
                            </RadioButtonInput>
                        </div>
                        <div class="label-details pp-grid-border-bottom pp-grid-border-left">Plan Comments</div>
                        <div class="details-span-3 pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">
                            <TextInput 
                                :rows=2
                                v-model:textValue="formData.newPaymentPlan.comment" 
                                :cssFramework="'vanilla'"  
                            />
                        </div>
                        <div class="label-details pp-grid-border-left pp-grid-border-bottom">Plan Flags</div>
                        <div class="details-span-3 pp-grid-border-left pp-grid-border-right pp-grid-border-bottom">
                            <Checkbox v-model="formData.newPaymentPlan.recurringPayments" :disabled="recurringDisabled" :title="'Recurring payments'"/>                         
                            <span label :class="recurringClass">Recurring payments</span>
                            <Checkbox v-model="formData.newPaymentPlan.excludeClaimsFromStatements" :title="'Exclude claims from statements'" @update:modelValue="SetExcludeStatement" />                            
                            <span label class="ps-1 pe-3">Exclude claims from statements</span>
                            <Checkbox v-model="formData.newPaymentPlan.doNotSendStatement" :title="'Do not send statement'" @update:modelValue="SetSendStatement" />
                            <span label class="ps-1">Do not send statement</span> 
                        </div>
                    </div>
                </div>
            </template> 
        </ActionFoldingPanel>
        <div v-if="(formData.planSummary?.length ?? 0) > 0">
            <ActionFoldingPanel panelTitle="Payment Plan Installment Summary"> 
                <template #body>  
                    <div class="skinnyscroll label-isalus-small" :style="{ 'width': ((planDetailsWidth / 2) + 'rem')}">
                        <div class="grid-container-summary">
                            <div class="text-center label-summary pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">#</div>
                            <div class="text-center label-summary pp-grid-border-top pp-grid-border-bottom pp-grid-border-left">Due Date</div>
                            <div class="text-center label-summary pp-grid-border-top pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">Payment Amount</div>
                        </div>
                        <div class="grid-container-summary" v-if="(formData.planSummary?.length ?? 0) > 0" v-for="(summaryRecord,index) in formData.planSummary">
                            <div class="text-center value-summary pp-grid-border-bottom pp-grid-border-left">{{summaryRecord.installmentID}}</div>
                            <div class="text-center value-summary pp-grid-border-bottom pp-grid-border-left">{{summaryRecord.installmentDate}}</div>
                            <div class="text-end value-summary pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">{{prettyCurrency(summaryRecord.installmentAmount)}}</div>
                        </div>
                        <div class="grid-container-summary">
                            <div class="text-end fw-bold total-summary pp-grid-border-bottom pp-grid-border-left">Total:</div>
                            <div class="text-end fw-bold value-summary pp-grid-border-bottom pp-grid-border-left pp-grid-border-right">{{prettyCurrency(formData.newPaymentPlan.totalAmount)}}</div>
                        </div>
                    </div>
                </template> 
            </ActionFoldingPanel>  
        </div>  
    </div>
    <div :style="{'overflow': 'hidden', 'height': (formData.window.footerHeight + 'rem')}">  
        <div class="row pt-2 border-top"> 
            <div class="col-9">
                <span v-show="(formData.planErrorMessage.isError && !planDisabled)" class="fst-italic label-isalus-small text-danger">{{formData.planErrorMessage.message}}</span>
            </div>
            <div class="col-3 text-end">
                <ActionButtonV2 
                    :text="'Add Card'"
                    :action="ActionButtonStyle.CreditCard"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Adding'"
                    :performAction="addCreditCard"
                    :endingPadding="1"/>
                <ActionButtonV2 
                    :disabled="formData.planErrorMessage.isError"
                    :action="ActionButtonStyle.Save"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Saving'"
                    :performAction="savePaymentPlan"
                    :endingPadding="1"/>
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Closing'"
                    :performAction="closeWindow"/>
            </div>
        </div>
    </div>
    <div v-if="formData.modalDisplayAddCard">
        <PaymentPlanCreditCard
            :show="true"
            :patientId="props.patientId"
            :patientName="formData.patientInformation?.patientName"
            :paymentMethodId="formData.newPaymentPlan.paymentMethodId"
            :refreshCards="formData.refreshCards"
            @onDisplay="onDisplayCreditCard" 
        />
    </div>
</template>